<script setup lang="ts">
import { ref, type HTMLAttributes } from 'vue'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from 'radix-vue'
import { storeToRefs } from 'pinia'
import { cn } from '@/lib/utils'
import { useGameState } from '@/stores/gameState'
import MenuIcon from '@/assets/menu.svg'
import ExitIcon from '@/assets/exit.svg'
import ShowCardsIcon from '@/assets/show-cards.svg'
import ControlsIcon from '@/assets/controls.svg'
import CloseIcon from '@/assets/X.svg'
import ShareIcon from '@/assets/icons/share2.svg'
import EditIcon from '@/assets/icons/edit.svg'
import CheckBox from '@/components/ui/CheckBox.vue'
import { useCommentaryParamsModal } from '@/stores/commentaryParamsModal'
import UpdateCommentaryParamsDialog from './UpdateCommentaryParamsDialog.vue'

const { isHintMode, playerCardsVisible, gameMode, myPlayerData } = storeToRefs(useGameState())
const { isModalOpen } = storeToRefs(useCommentaryParamsModal())

const emit = defineEmits(['exit', 'exit-and-close'])

const props = defineProps<{
  class?: HTMLAttributes['class']
}>()

const isMenuVisible = ref(false)

const copyTableLink = () => {
  isMenuVisible.value = false
}

const handleCommentaryParamsModelOpen = () => {
  isModalOpen.value = true
  isMenuVisible.value = false
}
</script>

<template>
  <dropdown-menu-root v-model:open="isMenuVisible">
    <dropdown-menu-trigger
      :class="cn('bg-background p-2 focus:outline-hidden', props.class)"
      aria-label="Customise options"
    >
      <slot :isMenuVisible="isMenuVisible">
        <div class="relative w-7 h-7">
          <transition name="options-menu">
            <close-icon v-if="isMenuVisible" class="absolute w-7 h-7" />
            <menu-icon v-else class="absolute w-7 h-7" />
          </transition>
        </div>
      </slot>
    </dropdown-menu-trigger>
    <dropdown-menu-portal>
      <dropdown-menu-content
        class="z-9999 min-w-[298px] desktop:mr-[86px] outline-hidden bg-light-grey rounded-xl p-2 mr-4 transition-all will-change-[opacity,transform] data-[side=bottom]:animate-slideUpAndFade"
      >
        <div>
          <dropdown-menu-item
            v-if="gameMode === 'drill'"
            value="showHints"
            class="cursor-pointer mb-[6px] p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center gap-2"
            @select.prevent="isHintMode = !isHintMode"
          >
            <controls-icon />
            <div>{{ $t('OptionsMenu.button.Hints') }}</div>
            <div class="grow" />
            <check-box :checked="isHintMode" />
          </dropdown-menu-item>
          <dropdown-menu-item
            v-if="gameMode === 'drill'"
            value="showCards"
            class="cursor-pointer p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center gap-2"
            @select.prevent="playerCardsVisible = !playerCardsVisible"
          >
            <show-cards-icon />
            <div>{{ $t('OptionsMenu.button.ShowCards') }}</div>
            <div class="grow" />
            <check-box :checked="playerCardsVisible" />
          </dropdown-menu-item>
          <dropdown-menu-item
            v-if="gameMode === 'compete'"
            value="copyTableLink"
            class="cursor-pointer p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center gap-2"
            @select.prevent="copyTableLink()"
          >
            <share-icon />
            <div>{{ $t('OptionsMenu.button.CopyTableLink') }}</div>
          </dropdown-menu-item>
          <dropdown-menu-separator class="h-px bg-divider my-2" />
          <dropdown-menu-item
            value="exit"
            class="cursor-pointer p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center gap-2 mb-[6px]"
            @select.prevent="
              () => {
                emit('exit')
                isMenuVisible = false
              }
            "
          >
            <exit-icon />
            <div>
              {{
                gameMode === 'drill'
                  ? $t('OptionsMenu.button.EndPractice')
                  : $t('OptionsMenu.button.LeaveTable')
              }}
            </div>
          </dropdown-menu-item>
          <dropdown-menu-item
            v-if="gameMode === 'compete' && myPlayerData?.host"
            value="exit-and-close"
            class="cursor-pointer p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center gap-2"
            @select.prevent="
              () => {
                emit('exit-and-close')
                isMenuVisible = false
              }
            "
          >
            <close-icon class="w-[22px] h-[22px]" />
            <div>
              {{ $t('OptionsMenu.button.LeaveAndCloseTable') }}
            </div>
          </dropdown-menu-item>
          <dropdown-menu-separator class="h-px bg-divider my-2" />
          <dropdown-menu-item
            value="updateCommentaryParams"
            class="cursor-pointer p-2 rounded-[8px] bg-menu-item-bg text-white flex items-center gap-2"
            @select.prevent="handleCommentaryParamsModelOpen"
          >
            <edit-icon />
            <div>
              {{ $t('OptionsMenu.button.UpdateCommentaryParams') }}
            </div>
          </dropdown-menu-item>
        </div>
      </dropdown-menu-content>
    </dropdown-menu-portal>
  </dropdown-menu-root>
  <UpdateCommentaryParamsDialog :isOpen="isModalOpen" />
</template>

<style lang="scss">
.options-menu {
  &-enter-active,
  &-leave-active {
    transition: all 0.1s ease-out;
  }

  &-enter-from {
    opacity: 0;
  }

  &-leave-to {
    opacity: 0;
  }
}
</style>
